import {
  useThemePagesQuery,
  type ThemePagesQueryResponse,
  type ThemePagesQueryVariables,
} from '~/api/graphql/queries/themePages.generated';
import type { IVariantTemplate } from '~/modules/campaign/types';
import type { InfiniteData } from '@tanstack/react-query';
import type { TemplateType } from '~/types/template';
import type { IThemePageManagement } from '../types';

import { useCampaignTemplateListing, useQueryCampaignTemplates } from '~/modules/campaign-template';
import { convertTemplateTypeToGPV7, getSimilarIdFromString, mapThemePagesV7 } from '~/utils';
import { useCampaignGroupTemplateListing } from '~/modules/campaign-group-template';
import { useInfinityThemePageManagement } from './useInfinityThemePageManagement';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { LIMIT_THEME_PAGE_MANAGEMENT_PAGINATION } from '../constants';
import { useQueryCampaignGroups } from '~/modules/campaign-group';
import { DEFAULT_QUERY_STALE_TIME } from '~/api/configs';
import { useQueryClient } from '@tanstack/react-query';
import { IThemePageType } from '~/types/themePage';
import { useQueryThemeInfo } from '~/modules/apps';
import { useAsyncWithErrorToast } from '~/hooks';
import { useMemo } from 'react';

export const useQueryThemePageManagement = (props?: {
  limit?: number;
  queryValue?: string;
  pageTypes?: TemplateType[];
}) => {
  const { limit = LIMIT_THEME_PAGE_MANAGEMENT_PAGINATION, queryValue, pageTypes } = props || {};

  const queryClient = useQueryClient();
  const { themeId } = useQueryThemeInfo();
  const { data: listCampaignTemplate = [] } = useCampaignTemplateListing();
  const { data: listCGTemplate = [] } = useCampaignGroupTemplateListing();
  const { handleRefetch: refetchCampaignListing } = useQueryCampaignTemplates();
  const { refetch: refetchCampaignGroupListing } = useQueryCampaignGroups();
  const withErrorToast = useAsyncWithErrorToast();

  const pageTypeIn = pageTypes
    ?.map((type) => convertTemplateTypeToGPV7(type))
    ?.filter((type) => !!type);

  const trimmedQueryValue = queryValue?.trim() ?? '';

  const variables: ThemePagesQueryVariables = {
    first: limit,
    where: {
      or: [
        { nameContainsFold: trimmedQueryValue },
        { id: getSimilarIdFromString(trimmedQueryValue) },
      ],
      themeID: themeId,
      typeNotIn: [IThemePageType.STATIC],
      parentIDNotNil: true,
      typeIn: pageTypeIn,
    },
    orderBy: {
      field: 'CREATED_AT',
      direction: 'DESC',
    },
  };

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
  } = useInfinityThemePageManagement(variables, {
    enabled: !!themeId,
    keepPreviousData: true,
    staleTime: DEFAULT_QUERY_STALE_TIME,
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage.themePages?.pageInfo?.hasNextPage;
      return hasNextPage ? lastPage.themePages?.pageInfo?.endCursor : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      const hasPreviousPage = firstPage.themePages?.pageInfo?.hasPreviousPage;
      return hasPreviousPage ? firstPage.themePages?.pageInfo?.startCursor : undefined;
    },
  });

  const mappedData: IThemePageManagement[] = useMemo(() => {
    if (!data) return [];

    const pages = data?.pages;
    const themePages = pages?.flatMap((page) => page.themePages?.edges)?.filter((page) => !!page);
    const themePagesMapping = mapThemePagesV7(themePages);

    const listNotStoppedCampaignTemplate = listCampaignTemplate.filter(
      (campaign) => campaign.status !== CAMPAIGN_STATUS.STOPPED,
    );

    const listNotStoppedCampaignGroup = listCGTemplate.filter(
      (campaignGroup) => campaignGroup.status !== CAMPAIGN_STATUS.STOPPED,
    );

    return themePagesMapping.map((page) => {
      let campaignInfoVariant: IVariantTemplate | undefined;
      const campaign = listNotStoppedCampaignTemplate.find((campaign) =>
        campaign.variants?.some((variant) => {
          const isMatch = variant?.themePageID === page.id;
          if (isMatch) campaignInfoVariant = variant;
          return isMatch;
        }),
      );

      if (campaign) {
        return {
          ...page,
          campaignInfo: {
            id: campaign.id,
            name: campaign.name ?? '',
            type: CAMPAIGN_TYPE.TEMPLATE,
            status: campaign.status,
            variant: campaignInfoVariant,
          },
        };
      }

      let campaignGroupInfoVariant: IVariantTemplate | undefined;
      const campaignGroup = listNotStoppedCampaignGroup.find((campaignGroupItem) =>
        campaignGroupItem.campaigns?.some(
          (campaign) =>
            !campaign.deletedAt &&
            campaign.variants?.some((variant) => {
              const isMatch = variant?.themePageID === page.id;
              if (isMatch) campaignGroupInfoVariant = variant;
              return isMatch;
            }),
        ),
      );

      if (campaignGroup) {
        return {
          ...page,
          campaignInfo: {
            id: campaignGroup.id,
            name: campaignGroup.title ?? '',
            type: CAMPAIGN_TYPE.TEMPLATE_BUNDLE,
            status: campaignGroup.status,
            variant: campaignGroupInfoVariant,
          },
        };
      }

      return page;
    });
  }, [listCGTemplate, listCampaignTemplate, data]);

  const totalCount = data?.pages?.[0]?.themePages?.totalCount ?? 0;

  const resetInfiniteQueryPagination = (): void => {
    const queryKey = useThemePagesQuery.getKey(variables);

    queryClient.setQueryData<InfiniteData<ThemePagesQueryResponse>>(queryKey, (oldData) => {
      if (!oldData) return undefined;

      return {
        ...oldData,
        pages: oldData.pages.slice(0, 1),
        pageParams: oldData.pageParams.slice(0, 1),
      };
    });
  };

  const handleFetchNextPage = async () => {
    if (isFetchingNextPage || !hasNextPage) return;
    await fetchNextPage();
  };

  const handleFetchPreviousPage = async () => {
    if (isFetchingPreviousPage || !hasPreviousPage) return;
    await fetchPreviousPage();
  };

  const handleRefetch = () =>
    withErrorToast(
      async () => {
        await Promise.all([refetchCampaignListing(), refetchCampaignGroupListing(), refetch()]);
        resetInfiniteQueryPagination();
      },
      { source: 'useQueryThemePageManagement.handleRefetch', tags: { app: 'App' } },
    );

  return {
    data: mappedData,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    handleRefetch,
    handleFetchNextPage,
    handleFetchPreviousPage,
  };
};
